import {autoRegister, resolve} from "../../../container";
import type {Nullable} from "../../../bootstrap/common/types";
import {PageFeatures} from "../pageFeatures";
import type {EopPopover} from "./popover";
import {Page} from "../../../common/page";

@autoRegister()
export class PopoverService {

    private popoverAnchors: Map<string, HTMLElement> = new Map();

    public constructor(
        private pageFeatures: PageFeatures = resolve(PageFeatures),
        private page: Page = resolve(Page)
    ) {
    }

    public register(popoverId: string, anchorElement: HTMLElement): void {
        this.popoverAnchors.set(popoverId, anchorElement);
    }

    public unregister(popoverId: string): void {
        this.popoverAnchors.delete(popoverId);
    }

    public toggle(popoverId: string): void {
        const eopPopover = this.popover(popoverId);
        const anchorElement = this.getAnchorElement(popoverId);
        if (eopPopover && anchorElement) {
            this.setPopoverPosition(anchorElement, eopPopover);
            eopPopover.toggle(anchorElement);
        }
    }

    public open(popoverId: string): void {
        const eopPopover = this.popover(popoverId);
        const anchorElement = this.getAnchorElement(popoverId);
        if (eopPopover && anchorElement) {
            this.setPopoverPosition(anchorElement, eopPopover);
            eopPopover.open(anchorElement);
        }
    }

    public close(popoverId: string): void {
        const eopPopover = this.popover(popoverId);
        if (eopPopover) {
            eopPopover.close();
        }
    }

    private popover(popoverId: string): Nullable<EopPopover> {
        return this.pageFeatures.findFirst(`eop-popover[popover-id="${popoverId}"]`);
    }

    private getAnchorElement(popoverId: string): Nullable<HTMLElement> {
        return this.popoverAnchors.get(popoverId) ?? null;
    }

    private setPopoverPosition(anchorElement: HTMLElement, popover: EopPopover): void {
        const currentAnchorElementSize = {
            width: anchorElement.getBoundingClientRect().width, // visible width, not the same as clientWidth
            height: anchorElement.getBoundingClientRect().height // visible height, not the same as clientHeight
        };

        const parentHeightProperty = popover.style.getPropertyValue("--parent-height");
        const parentWidthProperty = popover.style.getPropertyValue("--parent-width");
        if (parentHeightProperty === "0px" || parentWidthProperty === "0px" || parentHeightProperty === "" || parentWidthProperty === "") {
            popover.style.setProperty("--parent-height", currentAnchorElementSize.height + "px");
            popover.style.setProperty("--parent-width", currentAnchorElementSize.width + "px");
        }

        const topOffset = anchorElement.topOffset();
        const leftOffset = anchorElement.leftOffset();
        const rightOffset = anchorElement.rightOffset();
        const offsetFromRight = this.page.viewportWidth() - rightOffset;
        popover.style.setProperty("--parent-center-top-offset", topOffset + 0.5 * currentAnchorElementSize.height + "px");
        popover.style.setProperty("--parent-center-left-offset", leftOffset + 0.5 * currentAnchorElementSize.width + "px");
        popover.style.setProperty("--parent-center-right-viewport-offset", offsetFromRight + 0.5 * currentAnchorElementSize.width + "px");
    }
}